import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Calendar,
  Download,
  BarChart3,
  Activity,
  DollarSign,
  RefreshCw,
  Users,
  ArrowDownToLine,
  ArrowUpFromLine,
  Wallet,
  Clock,
  CheckCircle,
} from 'lucide-react';
import { format, startOfWeek } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { reportsApi } from '@/api/reports.api';
import { useAgentStore } from '@/store/agentStore';
import { AgentHeader } from '@/components/layout/AgentHeader';
import { AgentSidebar } from '@/components/layout/AgentSidebar';
import { BottomNav } from '@/components/layout/BottomNav';
import toast from 'react-hot-toast';

type ReportPeriod = 'daily' | 'weekly' | 'monthly';

export default function DailyReportsPage() {
  const { agent } = useAgentStore();
  const queryClient = useQueryClient();
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [period, setPeriod] = useState<ReportPeriod>('daily');
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generateDate, setGenerateDate] = useState(
    format(new Date(Date.now() - 86400000), 'yyyy-MM-dd')
  );

  // Dashboard report for daily view — uses the new endpoint
  const { data: dashboard, isLoading: dashLoading, isError: dashError } = useQuery({
    queryKey: ['dashboardReport', selectedDate],
    queryFn: () => reportsApi.getDashboardReport(selectedDate),
    enabled: period === 'daily',
    retry: 1,
  });

  // Weekly / Monthly period reports
  const { data: periodReport, isLoading: periodLoading, isError: periodError } = useQuery({
    queryKey: ['periodReport', period, selectedDate],
    queryFn: async () => {
      if (period === 'weekly') {
        const d = new Date(selectedDate);
        const weekStart = startOfWeek(d, { weekStartsOn: 1 });
        return reportsApi.getWeeklyReport(format(weekStart, 'yyyy-MM-dd'));
      } else {
        const d = new Date(selectedDate);
        return reportsApi.getMonthlyReport(d.getMonth() + 1, d.getFullYear());
      }
    },
    enabled: period !== 'daily',
    retry: 1,
  });

  const isError = period === 'daily' ? dashError : periodError;

  // Generate report mutation
  const generateMutation = useMutation({
    mutationFn: (date: string) => reportsApi.generateReport(date),
    onSuccess: (_data, date) => {
      toast.success('Reporte generado exitosamente');
      setShowGenerateModal(false);
      // Switch to the generated date and refresh
      setSelectedDate(date);
      queryClient.invalidateQueries({ queryKey: ['dashboardReport'] });
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg ?? 'Error al generar el reporte');
    },
  });

  const handleExportPDF = async () => {
    try {
      toast.loading('Exportando reporte...');
      const blob = await reportsApi.exportPDF({ period, date: selectedDate });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `reporte-${agent?.agentCode}-${selectedDate}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.dismiss();
      toast.success('Reporte exportado');
    } catch {
      toast.dismiss();
      toast.error('Error al exportar reporte');
    }
  };

  const isLoading = period === 'daily' ? dashLoading : periodLoading;
  const chartColors = { cashIn: '#10b981', cashOut: '#ef4444', commissions: '#f59e0b' };

  // Unified data sources
  const summary = period === 'daily' ? dashboard?.summary : periodReport?.summary;
  const floatData =
    period === 'daily'
      ? dashboard?.float
      : periodReport?.float
        ? periodReport.float
        : periodReport?.openingFloat !== undefined
          ? {
              openingBalance: periodReport.openingFloat ?? 0,
              closingBalance: periodReport.closingFloat ?? 0,
              netChange: (periodReport.closingFloat ?? 0) - (periodReport.openingFloat ?? 0),
            }
          : undefined;

  // True if the API returned actual transaction data (not just zeros)
  const hasData =
    !isError &&
    summary != null &&
    (summary.totalTransactions > 0 || summary.cashInVolume > 0 || summary.cashOutVolume > 0);

  return (
    <div className="min-h-screen bg-gray-50">
      <AgentHeader />

      <div className="flex">
        <AgentSidebar />

        <main className="flex-1 pb-20 lg:pb-6">
          {/* Header */}
          <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <BarChart3 className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900">Reportes</h1>
                    <p className="text-sm text-gray-600">Análisis de rendimiento</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowGenerateModal(true)}
                    className="px-3 py-2 border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg flex items-center gap-2 text-sm transition-colors"
                  >
                    <RefreshCw className="w-4 h-4" />
                    <span className="hidden sm:inline">Generar</span>
                  </button>
                  <button
                    onClick={handleExportPDF}
                    className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center gap-2 transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    <span className="hidden sm:inline">Exportar PDF</span>
                  </button>
                </div>
              </div>
            </div>
          </header>

          {/* Main Content */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
            {/* Period Selector */}
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
                {/* Period Tabs */}
                <div className="flex gap-2">
                  {[
                    { value: 'daily', label: 'Diario' },
                    { value: 'weekly', label: 'Semanal' },
                    { value: 'monthly', label: 'Mensual' },
                  ].map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setPeriod(option.value as ReportPeriod)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        period === option.value
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>

                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-gray-400" />
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {isLoading ? (
              <ReportSkeleton />
            ) : isError ? (
              <div className="bg-white rounded-lg border border-red-200 p-12 text-center">
                <BarChart3 className="w-16 h-16 text-red-300 mx-auto mb-4" />
                <p className="text-gray-700 font-medium mb-1">No se pudo cargar el reporte</p>
                <p className="text-sm text-gray-500 mb-4">
                  El reporte para esta fecha puede no existir aún. Genera uno para continuar.
                </p>
                <button
                  onClick={() => { setGenerateDate(selectedDate); setShowGenerateModal(true); }}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 transition-colors"
                >
                  Generar Reporte para {selectedDate}
                </button>
              </div>
            ) : hasData ? (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <MetricCard
                    title="Total Transacciones"
                    value={summary?.totalTransactions ?? 0}
                    icon={<Activity className="w-6 h-6 text-blue-600" />}
                    bgColor="bg-blue-50"
                    iconBg="bg-blue-100"
                  />
                  <MetricCard
                    title="Depósitos"
                    value={`$${(summary?.cashInVolume ?? 0).toLocaleString()}`}
                    subtitle={`${summary?.cashInCount ?? 0} transacciones`}
                    icon={<ArrowDownToLine className="w-6 h-6 text-green-600" />}
                    bgColor="bg-green-50"
                    iconBg="bg-green-100"
                  />
                  <MetricCard
                    title="Retiros"
                    value={`$${(summary?.cashOutVolume ?? 0).toLocaleString()}`}
                    subtitle={`${summary?.cashOutCount ?? 0} transacciones`}
                    icon={<ArrowUpFromLine className="w-6 h-6 text-red-600" />}
                    bgColor="bg-red-50"
                    iconBg="bg-red-100"
                  />
                  <MetricCard
                    title="Comisiones"
                    value={`$${(summary?.totalCommissions ?? 0).toLocaleString()}`}
                    icon={<DollarSign className="w-6 h-6 text-yellow-600" />}
                    bgColor="bg-yellow-50"
                    iconBg="bg-yellow-100"
                  />
                </div>

                {/* Hourly Activity (daily only) */}
                {period === 'daily' && dashboard?.hourlyActivity && dashboard.hourlyActivity.length > 0 && (
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
                      <Clock className="w-5 h-5 text-purple-600" />
                      Actividad por Hora
                    </h2>
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={dashboard.hourlyActivity}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="hour"
                          tickFormatter={(h: number) => `${String(h).padStart(2, '0')}:00`}
                        />
                        <YAxis />
                        <Tooltip
                          formatter={(value: number) => `$${value.toLocaleString()}`}
                          labelFormatter={(h: number) => `${String(h).padStart(2, '0')}:00`}
                        />
                        <Legend />
                        <Bar dataKey="cashIn" name="Depósitos" fill={chartColors.cashIn} radius={[4,4,0,0]} />
                        <Bar dataKey="cashOut" name="Retiros" fill={chartColors.cashOut} radius={[4,4,0,0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Trend chart (weekly / monthly) */}
                {period !== 'daily' && periodReport?.dailyBreakdown && (
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-6">
                      Tendencia de Transacciones
                    </h2>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={periodReport.dailyBreakdown}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="date"
                          tickFormatter={(date) => format(new Date(date), 'dd MMM', { locale: es })}
                        />
                        <YAxis />
                        <Tooltip
                          formatter={(value: number) => `$${value.toLocaleString()}`}
                          labelFormatter={(label) =>
                            format(new Date(label), 'dd MMMM yyyy', { locale: es })
                          }
                        />
                        <Legend />
                        <Line
                          type="monotone"
                          dataKey="commissions"
                          stroke={chartColors.commissions}
                          strokeWidth={2}
                          name="Comisiones"
                        />
                        <Line
                          type="monotone"
                          dataKey="transactions"
                          stroke={chartColors.cashIn}
                          strokeWidth={2}
                          name="Transacciones"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Cash In vs Cash Out */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-6">Volumen por Tipo</h2>
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart
                        data={[
                          { name: 'Depósitos', value: summary?.cashInVolume ?? 0 },
                          { name: 'Retiros', value: summary?.cashOutVolume ?? 0 },
                        ]}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip formatter={(value: number) => `$${value.toLocaleString()}`} />
                        <Bar dataKey="value" fill="#8884d8" radius={[4,4,0,0]}>
                          {[chartColors.cashIn, chartColors.cashOut].map((color, index) => (
                            <Cell key={`cell-${index}`} fill={color} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-6">
                      Distribución de Transacciones
                    </h2>
                    <ResponsiveContainer width="100%" height={250}>
                      <RechartsPieChart>
                        <Pie
                          data={[
                            { name: 'Depósitos', value: summary?.cashInCount ?? 0 },
                            { name: 'Retiros', value: summary?.cashOutCount ?? 0 },
                          ]}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {[chartColors.cashIn, chartColors.cashOut].map((color, index) => (
                            <Cell key={`cell-${index}`} fill={color} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </RechartsPieChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Float Summary */}
                {floatData && (
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <Wallet className="w-5 h-5 text-purple-600" />
                      Efectivo del Día
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-600 mb-1">Efectivo Inicial</p>
                        <p className="text-2xl font-bold text-gray-900">
                          ${(floatData.openingBalance ?? 0).toLocaleString()}
                        </p>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-600 mb-1">Efectivo Final</p>
                        <p className="text-2xl font-bold text-gray-900">
                          ${(floatData.closingBalance ?? 0).toLocaleString()}
                        </p>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-600 mb-1">Diferencia</p>
                        <p
                          className={`text-2xl font-bold ${
                            (floatData.netChange ?? 0) >= 0 ? 'text-green-600' : 'text-red-600'
                          }`}
                        >
                          ${(floatData.netChange ?? 0).toLocaleString()}
                        </p>
                        {floatData.status && (
                          <span
                            className={`text-xs font-medium px-2 py-0.5 rounded-full mt-1 inline-block ${
                              floatData.status === 'OK'
                                ? 'bg-green-100 text-green-700'
                                : floatData.status === 'LOW'
                                ? 'bg-yellow-100 text-yellow-700'
                                : 'bg-red-100 text-red-700'
                            }`}
                          >
                            {floatData.status}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Top Customers (daily only) */}
                {period === 'daily' && dashboard?.topCustomers && dashboard.topCustomers.length > 0 && (
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <Users className="w-5 h-5 text-purple-600" />
                      Top Clientes del Día
                    </h2>
                    <div className="space-y-3">
                      {dashboard.topCustomers.map((customer, idx) => (
                        <div key={customer.phone ?? idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                              <span className="text-sm font-bold text-purple-600">{idx + 1}</span>
                            </div>
                            <div>
                              <p className="font-medium text-gray-900 text-sm">{customer.name}</p>
                              <p className="text-xs text-gray-500">{customer.phone}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-gray-900 text-sm">${(customer.volume ?? 0).toLocaleString()}</p>
                            <p className="text-xs text-gray-500">{customer.count} txns</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recent Transactions (daily only) */}
                {period === 'daily' && dashboard?.recentTransactions && dashboard.recentTransactions.length > 0 && (
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <Clock className="w-5 h-5 text-purple-600" />
                      Transacciones Recientes
                    </h2>
                    <div className="space-y-3">
                      {dashboard.recentTransactions.map((txn) => (
                        <div key={txn.id} className="flex items-center justify-between p-3 border border-gray-100 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className={`w-9 h-9 rounded-full flex items-center justify-center ${txn.type === 'CASH_IN' ? 'bg-green-100' : 'bg-red-100'}`}>
                              {txn.type === 'CASH_IN'
                                ? <ArrowDownToLine className="w-4 h-4 text-green-600" />
                                : <ArrowUpFromLine className="w-4 h-4 text-red-600" />}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900 text-sm">{txn.customerName}</p>
                              <p className="text-xs text-gray-500">{txn.reference}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className={`font-semibold text-sm ${txn.type === 'CASH_IN' ? 'text-green-600' : 'text-red-600'}`}>
                              {txn.type === 'CASH_IN' ? '+' : '-'}${(txn.amount ?? 0).toLocaleString()}
                            </p>
                            <p className="text-xs text-gray-500">
                              Comisión: ${(txn.commission ?? 0).toLocaleString()}
                            </p>
                            <div className="flex items-center gap-1 justify-end mt-0.5">
                              <CheckCircle className={`w-3 h-3 ${txn.status === 'completed' ? 'text-green-500' : 'text-gray-400'}`} />
                              <span className="text-xs text-gray-500">{txn.status}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
                <BarChart3 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-700 font-medium mb-1">Sin transacciones para esta fecha</p>
                <p className="text-sm text-gray-500 mb-4">
                  No se registraron transacciones el {selectedDate}. Prueba con otra fecha o genera
                  un reporte si acabas de cerrar el día.
                </p>
                <button
                  onClick={() => { setGenerateDate(selectedDate); setShowGenerateModal(true); }}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 transition-colors"
                >
                  Generar Reporte
                </button>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Generate Report Modal */}
      {showGenerateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Generar Reporte</h3>
            <p className="text-sm text-gray-600 mb-4">
              Selecciona la fecha para la que deseas generar el reporte diario.
            </p>
            <input
              type="date"
              value={generateDate}
              onChange={(e) => setGenerateDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent mb-4"
            />
            <div className="flex gap-3">
              <button
                onClick={() => setShowGenerateModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  generateMutation.mutate(generateDate);
                  setShowGenerateModal(false);
                }}
                disabled={generateMutation.isPending}
                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {generateMutation.isPending ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : null}
                Generar
              </button>
            </div>
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
}

// Componentes auxiliares
interface MetricCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  bgColor: string;
  iconBg: string;
}

function MetricCard({ title, value, subtitle, icon, bgColor, iconBg }: MetricCardProps) {
  return (
    <div className={`${bgColor} rounded-lg p-6 border border-gray-200`}>
      <div className="flex items-center justify-between mb-4">
        <div className={`${iconBg} p-3 rounded-lg`}>{icon}</div>
      </div>
      <div>
        <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        {subtitle && <p className="text-xs text-gray-600 mt-1">{subtitle}</p>}
      </div>
    </div>
  );
}

function ReportSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-32 bg-gray-200 rounded-lg" />
        ))}
      </div>
      <div className="h-96 bg-gray-200 rounded-lg" />
    </div>
  );
}
